import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import type { SectionUserRef } from '../../../../types/tender';
import { cleanHtmlDisplay } from '../utils';

interface SectionContentViewProps {
  content?: string;
  lastEditedBy?: SectionUserRef | undefined;
}

const SectionContentView: React.FC<SectionContentViewProps> = ({ content, lastEditedBy }) => {
  const theme = useTheme();

  const formattedDate = React.useMemo(() => {
    if (!lastEditedBy?.at) {
      return 'Unknown date';
    }
    const dateVal = lastEditedBy.at;
    if (typeof dateVal === 'object' && dateVal !== null && 'seconds' in dateVal) {
      return new Date((dateVal as { seconds: number }).seconds * 1000).toLocaleString();
    }
    return new Date(dateVal).toLocaleString();
  }, [lastEditedBy?.at]);

  return (
    <>
      <Box
        sx={{
          bgcolor: 'background.default',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          p: 2,
          borderRadius: 1,
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2,
            border: '1px solid #ddd',
          },
          '& th, & td': { border: '1px solid #ddd', p: 1 },
          '& th': {
            backgroundColor: theme.palette.greyContrast,
            color: theme.palette.primary.contrastText,
            fontWeight: 'bold',
          },
          // Headings - Consistent Chip Size (0.75rem ~ 12px)
          '& h1, & h2, & h3, & h4, & h5': {
            fontWeight: 'bold',
            position: 'relative',
            paddingLeft: '2.5rem',
          },
          '& h1': {
            fontSize: '1.8em',
            borderBottom: '2px solid #eee',
            pb: 1,
            mb: 2,
            '&::before': {
              content: '"H1"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: '#D05C09', // Brick
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& h2': {
            fontSize: '1.5em',
            mt: 3,
            mb: 1.5,
            '&::before': {
              content: '"H2"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: '#812A16', // Rust
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& h3': {
            fontSize: '1.25em',
            mt: 2,
            mb: 1,
            '&::before': {
              content: '"H3"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#000',
              backgroundColor: '#d39951', // Camel
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& h4': {
            fontSize: '1.1em',
            mt: 1.5,
            mb: 0.5,
            '&::before': {
              content: '"H4"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#000',
              backgroundColor: '#FF6800', // Orange
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
          '& h5': {
            fontSize: '1em',
            mt: 1,
            mb: 0.5,
            '&::before': {
              content: '"H5"',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '900',
              color: '#fff',
              backgroundColor: '#231e21', // Charcoal
              padding: '2px 6px',
              borderRadius: '4px',
              lineHeight: 1,
            },
          },
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized
        dangerouslySetInnerHTML={{
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          __html: cleanHtmlDisplay(content || ''),
        }}
      />
      {lastEditedBy && (
        <Typography
          variant="caption"
          display="block"
          align="right"
          sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}
        >
          Last edited by {lastEditedBy.displayName} on {formattedDate}
        </Typography>
      )}
    </>
  );
};

export default SectionContentView;
